/**
 * QuotaExceededModal — friendly modal for the structured
 * `annotation_quota_exceeded` errors emitted by the backend. Renders
 * one message per `scope`, with plain-English copy that names the
 * limit + suggests an admin contact.
 *
 * Wire-shape it consumes (apierror.QuotaExceeded on the Go side):
 *
 *   {
 *     code:    "annotation_quota_exceeded",
 *     scope:   "org_project_count" | "project_image_count"
 *            | "image_size_mb"     | "org_storage_bytes",
 *     current: <number>,
 *     limit:   <number>
 *   }
 *
 * Used by:
 *   - ProjectList.tsx        — project-create 409 with scope=org_project_count
 *   - ImageUploadModal.tsx   — presign 4xx for each image-quota scope
 *
 * Standalone-only UI surface; the Portal LabelFactory page has its own
 * mirror of this component (in ignode-ui).
 */
import { Button, Modal } from './ui';

export type QuotaScope =
    | 'org_project_count'
    | 'project_image_count'
    | 'project_class_count'
    | 'image_size_mb'
    | 'org_storage_bytes';

export interface QuotaExceededInfo {
    scope: QuotaScope | string;
    current: number;
    limit: number;
}

interface QuotaExceededModalProps {
    open: boolean;
    info: QuotaExceededInfo | null;
    onClose: () => void;
    isDarkMode?: boolean;
}

/** Format byte counts as human-readable (MB / GB) — matches the
 *  formatting the existing Uploads page uses elsewhere. */
function humanBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}

/** Build the title + body copy for a given quota scope. Returns null
 *  for unknown scopes so the caller falls back to a generic banner
 *  instead of crashing the UI.
 *
 *  Exported so consumers that prefer an inline banner over the modal
 *  (e.g. AddClassModal renders the error inside the existing form
 *  instead of stacking a second dialog) can share the wording. */
export function copyFor(info: QuotaExceededInfo): { title: string; body: string } | null {
    switch (info.scope) {
        case 'org_project_count':
            return {
                title: 'Project limit reached',
                body:
                    `You've reached the project limit for this org ` +
                    `(${info.current} of ${info.limit} projects). ` +
                    `Delete an existing project, or contact your administrator to raise the limit.`,
            };
        case 'project_image_count':
            return {
                title: 'Image limit reached',
                body:
                    `This project has ${info.current} images ` +
                    `(limit ${info.limit}). Delete some images, ` +
                    `or contact your administrator to raise the per-project limit.`,
            };
        case 'project_class_count':
            return {
                title: 'Class limit reached',
                body:
                    `This project has ${info.current} classes ` +
                    `(limit ${info.limit}). Delete an existing class, ` +
                    `or contact your administrator to raise the per-project limit.`,
            };
        case 'image_size_mb':
            return {
                title: 'Image too large',
                body:
                    `This image is ${info.current} MB. ` +
                    `Images must be ${info.limit} MB or smaller. ` +
                    `Resize or compress the image and try again.`,
            };
        case 'org_storage_bytes': {
            const pct = info.limit > 0
                ? Math.min(100, Math.round((info.current / info.limit) * 100))
                : 100;
            return {
                title: 'Org storage limit reached',
                body:
                    `Your org is using ${humanBytes(info.current)} of ` +
                    `${humanBytes(info.limit)} (${pct}%). ` +
                    `Free up space in Uploads or Label Factory projects, ` +
                    `or contact your administrator to raise the limit.`,
            };
        }
        default:
            return null;
    }
}

export default function QuotaExceededModal({ open, info, onClose, isDarkMode }: QuotaExceededModalProps) {
    if (!info) return null;
    const c = copyFor(info);
    // Unknown scope — render a generic shape so we never crash the UI.
    const title = c?.title ?? 'Quota exceeded';
    const body  = c?.body  ?? `A platform limit was reached (${info.scope}: ${info.current} / ${info.limit}). Contact your administrator.`;

    return (
        <Modal
            open={open}
            onClose={onClose}
            isDarkMode={isDarkMode}
            title={title}
            widthClass="max-w-md"
            footer={
                <Button variant="primary" onClick={onClose}>OK</Button>
            }
        >
            <p className={isDarkMode ? 'text-gray-300 text-sm' : 'text-gray-700 text-sm'}>
                {body}
            </p>
        </Modal>
    );
}

/**
 * extractQuotaInfo — helper that tries to pull a QuotaExceededInfo out of
 * an unknown error value. Returns null when the error isn't a structured
 * quota error (caller falls back to a generic banner).
 *
 * Accepts:
 *   - APIClientError (annotatorApi flow) — checks code/body
 *   - UploadFlowError (uploadFlow stage='presign') — checks apiCode/body
 *   - Plain objects with the same shape (defensive)
 */
export function extractQuotaInfo(err: unknown): QuotaExceededInfo | null {
    if (!err || typeof err !== 'object') return null;
    // Both APIClientError and UploadFlowError expose either:
    //   .body.code === 'annotation_quota_exceeded' + .body.scope/.current/.limit
    //   .apiCode === 'annotation_quota_exceeded' (with body on the source error)
    // We probe both shapes.
    // Cast to a permissive shape — runtime type guards below validate.
    type Probed = {
        body?: { code?: string; scope?: string; current?: number; limit?: number };
        apiCode?: string;
        code?: string;
        scope?: string;
        current?: number;
        limit?: number;
    };
    const e = err as Probed;
    const code = e.body?.code ?? e.apiCode ?? e.code;
    if (code !== 'annotation_quota_exceeded') return null;
    const scope = e.body?.scope ?? e.scope;
    const current = e.body?.current ?? e.current;
    const limit = e.body?.limit ?? e.limit;
    if (typeof scope !== 'string' || typeof current !== 'number' || typeof limit !== 'number') {
        return null;
    }
    return { scope: scope as QuotaScope, current, limit };
}
